import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ReferralDrawer } from '../../components/referral-drawer/ReferralDrawer';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  exportOpenReferrals,
  exportClosedReferrals,
  fetchReferralFilterOptions,
  fetchReferrals,
  fetchClosedReferrals,
  fetchClosedReferralFilterOptions,
} from '../../services/referralService';
import { ExportButton } from '../../components/ui/ExportButton';
import { useWorkflowBar } from '../../contexts/WorkflowBarContext';
import { ReferralTable, DEFAULT_SORT, REFERRAL_COLUMNS, getReferralSelectionId, getVisibleReferralColumns } from '../../components/referrals/ReferralTable';
import { useReferralList } from '../../hooks/useReferralList';
import { getCurrentUser, getOrgType, isSmartLightEmployee } from '../../authUtils';


function ReferralListPage() {
  const {
    setCounts, setOverdueCounts, setTotalReferrals, setVisible,
    workflowStepFilter, assignedToFilter, qsActive,
    stepLabels, setStepLabels,
    isClosedFilter,
  } = useWorkflowBar();

  const { searchText = '' } = useOutletContext() || {};

  const fetchFn = isClosedFilter ? fetchClosedReferrals : fetchReferrals;
  const fetchOptionsFn = isClosedFilter
    ? fetchClosedReferralFilterOptions
    : fetchReferralFilterOptions;

  const {
    referrals,
    totalCount,
    loading,
    error,
    sortColumns,
    setSortColumns,
    isDefaultSort,
    setIsDefaultSort,
    openSortMenu,
    setOpenSortMenu,
    filters,
    setFilters,
    filterOptions,
    handleSort,
    handleClearColumnSort,
    patchReferral,
    reload,
    workflowCounts,
    refreshCounts,
  } = useReferralList(fetchFn, searchText, {
    fetchOptionsFn,
    workflowStepFilter,
    assignedToFilter,
    isQuestion: qsActive ? true : undefined,
  });

  const [drawerNeedsReload, setDrawerNeedsReload] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [filteredCount, setFilteredCount] = useState(0);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    if (drawerNeedsReload) {
      reload();
      setDrawerNeedsReload(false);
    }
  }, [drawerNeedsReload, reload]);

  const decrementedReferralIdsRef = useRef(new Set());

  // Sync the open drawer + workflow-bar counts when an activity is posted.
  // patchReferral updates the just-posted row (and clears open_question), and
  // the open drawer's selectedReferral is synced in lockstep — scoped to that
  // row so unrelated list refreshes never silently swap the drawer's data.
  //
  // Every post defers a full list reload to drawer close (PRODUCT-357) so the
  // re-filtered table drops any referral whose status moved it out of the active
  // filter. The two count strategies still branch on the Open Questions filter so
  // they never fight on the same post: when qsActive, optimistically decrement
  // OPEN_QUESTIONS once per referral (a server re-pull would return a stale count
  // because buildCountsParams intentionally omits is_question); otherwise re-pull
  // all counts from the server for a live badge update while the drawer is open.
  const handleActivityPosted = useCallback((referralId, patch) => {
    patchReferral({ referral_id: referralId, ...patch });

    setSelectedReferral((prev) => {
      if (!prev || getReferralSelectionId(prev) !== referralId) return prev;
      return { ...prev, ...patch, open_question: false };
    });

    // Any post can change the referral's status / workflow step, moving it out
    // of the currently-applied filter. Defer a full list reload to drawer close
    // (PRODUCT-357): the patched row stays visible behind the open drawer, then
    // on close the table re-filters and drops the row if it no longer matches —
    // keeping the visible results consistent with the workflow-step badge counts.
    setDrawerNeedsReload(true);

    if (qsActive) {
      const key = String(referralId);
      if (!decrementedReferralIdsRef.current.has(key)) {
        decrementedReferralIdsRef.current.add(key);
        setCounts(prev => ({
          ...prev,
          'OPEN_QUESTIONS': Math.max(0, (prev['OPEN_QUESTIONS'] || 0) - 1),
        }));
      }
    } else {
      // Refresh the badge counts live while the drawer is still open
      // (PRODUCT-282); the deferred reload on close re-pulls them too.
      refreshCounts();
    }
  }, [patchReferral, qsActive, setCounts, refreshCounts]);

  const handleRowDoubleClick = (referral) => {
    setSelectedReferral(referral);
    setDrawerOpen(true);
  };

  const handleFilteredCountChange = useCallback((count) => {
    setFilteredCount(count);
  }, []);
  const visibleColumns = getVisibleReferralColumns(REFERRAL_COLUMNS, {
    isInternal: isSmartLightEmployee(),
    orgType: getOrgType(),
    displayRules: getCurrentUser()?.display_rules,
  });

  // Drive the workflow bar from the server-computed counts (full filtered
  // dataset, not just the visible page). The counts response carries the
  // step labels too, so it's the single source for both labels and totals.
  useEffect(() => {
    if (!workflowCounts?.steps) return;
    const { steps, total_count: total = 0 } = workflowCounts;
    setStepLabels(steps.map((s) => ({ key: s.key, label: s.label })));
    setCounts(Object.fromEntries(steps.map((s) => [s.key, s.count ?? 0])));
    setOverdueCounts(Object.fromEntries(steps.map((s) => [s.key, s.overdue_count ?? 0])));
    setTotalReferrals(total);
    // Keep workflow status bar visible even when total_count is 0
    setVisible(true);
    decrementedReferralIdsRef.current.clear();
  }, [workflowCounts, setStepLabels, setCounts, setOverdueCounts, setTotalReferrals, setVisible]);

  // Hide the bar when leaving this page
  useEffect(() => {
    return () => setVisible(false);
  }, [setVisible]);

  useEffect(() => {
    if (loading || selectedRowId === null) return;
    const selectedReferralAvailable = referrals.some((referral) => getReferralSelectionId(referral) === selectedRowId);
    if (!selectedReferralAvailable) {
      setSelectedRowId(null);
    }
  }, [loading, referrals, selectedRowId]);

  return (
    <div className="bg-background font-sans antialiased -m-6 h-[calc(100vh-88px)] flex flex-col">
      {/* Referral detail drawer */}
      <ReferralDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        onActivityPosted={isClosedFilter ? undefined : handleActivityPosted}
        referral={selectedReferral}
        // SmartLight users get a read-only Carrier Portal — comment/status
        // creation happens only in the Carrier Engagement Dashboard, and a
        // read-only drawer keeps the portal screen-share-safe. External
        // (carrier/TPA/client) users keep the composer.
        readOnly={isClosedFilter || isSmartLightEmployee()}
      />
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-col flex-1 min-h-0">
        <div className="flex justify-end mb-0">
          <Button
            variant="ghost"
            size="sm"
            data-testid="default-sort-button"
            className={cn(
              'text-xs gap-1',
              isDefaultSort
                ? 'text-primary hover:bg-transparent hover:text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
            onClick={() => {
              if (isDefaultSort) {
                setSortColumns([]);
                setIsDefaultSort(false);
              } else {
                setSortColumns([]);
                setIsDefaultSort(true);
              }
            }}
          >
            <svg className="!h-3 !w-3" viewBox="5 5 14 14" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" strokeLinecap="round">
              <path d="M8.5 11l2.7-4.6a1 1 0 0 1 1.6 0L15.5 11" />
              <path d="M15.5 13l-2.7 4.6a1 1 0 0 1-1.6 0L8.5 13" />
            </svg>
            Prioritized Sort
          </Button>
        </div>
        <Card className="flex flex-col flex-1 min-h-0">
          <CardContent className="p-0 flex flex-col flex-1 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-12" aria-label="Loading">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-destructive">Error: {error}</p>
              </div>
            ) : (
              <div className="overflow-x-auto flex-1 min-h-0" data-testid="referral-list-table">
                <ReferralTable
                  columns={visibleColumns}
                  rows={referrals}
                  emptyStateText={searchText ? 'No results found for this search.' : 'No referrals found.'}
                  sortColumns={sortColumns}
                  setSortColumns={setSortColumns}
                  isDefaultSort={isDefaultSort}
                  setIsDefaultSort={setIsDefaultSort}
                  defaultSort={DEFAULT_SORT}
                  openSortMenu={openSortMenu}
                  setOpenSortMenu={setOpenSortMenu}
                  onSort={handleSort}
                  onClearColumnSort={handleClearColumnSort}
                  filters={filters}
                  filterOptions={filterOptions}
                  onFilterChange={(filterKey, val) => setFilters(prev => ({ ...prev, [filterKey]: val }))}
                  onDateFilterChange={(fromKey, toKey, from, to) => setFilters(prev => ({ ...prev, [fromKey]: from, [toKey]: to }))}
                  onRowDoubleClick={handleRowDoubleClick}
                  selectedRowId={selectedRowId}
                  onSelectedRowChange={setSelectedRowId}
                  stepLabels={stepLabels}
                  onFilteredCountChange={handleFilteredCountChange}
                />
              </div>
            )}
            {!loading && !error && referrals.length > 0 && (
              <div className="sticky bottom-0 z-10 flex items-center justify-center gap-4 border-t border-border bg-accent px-3 py-1">
                <span className="text-xs text-muted-foreground">
                  {filteredCount} of {totalCount} results
                </span>
                <ExportButton
                  exportFn={isClosedFilter ? exportClosedReferrals : exportOpenReferrals}
                  params={{
                    ...filters,
                    workflow_step: workflowStepFilter || undefined,
                    assigned_to: (assignedToFilter === 'smartlight' || assignedToFilter === 'tpa_carrier') ? assignedToFilter : undefined,
                    ordering: sortColumns
                      .map(s => (s.direction === 'desc' ? `-${s.field}` : s.field))
                      .join(',') || (isDefaultSort ? '-impact,due_date' : undefined),
                    search: searchText || undefined,
                  }}
                  filename={isClosedFilter ? 'closed_referrals_export.xlsx' : 'referrals_export.xlsx'}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export { ReferralListPage };
